import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CallBackProps, Step } from "react-joyride";
import { STATUS, EVENTS } from "react-joyride";
import {
  checkAndMarkOnboardingComplete,
  markTourCompleteOnServer,
  STEP_KEYS,
  getSavedStep,
  saveStep,
  clearStep,
  waitForOnboardingSync,
} from "../../../../../utils/onboarding";

interface UseCalendarTourOptions {
  calendarEditMode: boolean;
  setCalendarEditMode: (v: boolean) => void;
  specificDayDate: string | null;
  setSpecificDayDate: (v: string | null) => void;
  openBlockModalTick: boolean;
  setOpenBlockModalTick: (v: boolean) => void;
  setIsExitingEdit: (v: boolean) => void;
  setConfigDropdownOpen: (v: boolean) => void;
  setOpenScheduleModalTick: (v: number) => void;
  setCancelSchedulingTick: (v: number) => void;
  scheduleHasChanges: boolean;
  setScheduleHasChanges: (v: boolean) => void;
  setShowUnsavedModal: (v: boolean) => void;
  onTourRunningChange?: (running: boolean) => void;
  /** Psicóloga NZ recién creada, en estado "new", sin horarios reales cargados todavía.
   *  Para este grupo el paso simulado de carga de horarios (#8) corta la guía ahí en vez
   *  de continuar al tour de agendamiento — y en su lugar se les pide configurar su
   *  disponibilidad real. */
  isMandatoryScheduleOnboarding?: boolean;
}

export function useCalendarTour({
  calendarEditMode,
  setCalendarEditMode,
  specificDayDate,
  setSpecificDayDate,
  openBlockModalTick,
  setOpenBlockModalTick,
  setIsExitingEdit,
  setConfigDropdownOpen,
  setOpenScheduleModalTick,
  setCancelSchedulingTick,
  scheduleHasChanges,
  setScheduleHasChanges,
  setShowUnsavedModal,
  onTourRunningChange,
  isMandatoryScheduleOnboarding = false,
}: UseCalendarTourOptions) {
  const [canSkipTour, setCanSkipTour] = useState(false);

  // Config tour state
  const [runTour, setRunTour] = useState(false);
  const [stepIndex, setStepIndex] = useState<number>(() => {
    const saved = getSavedStep(STEP_KEYS.calendarConfig);
    return saved >= 0 ? saved : 0;
  });
  const [tourKey, setTourKey] = useState(0);
  const [mockSaveCompleted, setMockSaveCompleted] = useState(false);

  // Booking tour state
  const [runBookingTour, setRunBookingTour] = useState(false);
  const [bookingStepIndex, setBookingStepIndex] = useState<number>(() => {
    const saved = getSavedStep(STEP_KEYS.calendarBooking);
    return saved >= 0 ? saved : 0;
  });
  const [shouldStartBookingTour, setShouldStartBookingTour] = useState(false);
  const shouldStartBookingTourRef = useRef(shouldStartBookingTour);
  useEffect(() => {
    shouldStartBookingTourRef.current = shouldStartBookingTour;
  }, [shouldStartBookingTour]);

  // MutationObserver refs for booking tour
  const bookingObsRef = useRef<MutationObserver | null>(null);
  const bookingSlotObsRef = useRef<MutationObserver | null>(null);
  const bookingCancelObsRef = useRef<MutationObserver | null>(null);
  const bookingConfirmedRef = useRef(false);

  // Mini-tour: Specific Day Panel
  const [runSpecificDayTour, setRunSpecificDayTour] = useState(false);
  const [specificDayTourStep, setSpecificDayTourStep] = useState(0);
  const [specificDayTourKey, setSpecificDayTourKey] = useState(0);

  // Mini-tour: Block Schedule Panel
  const [runBlockTour, setRunBlockTour] = useState(false);
  const [blockTourStep, setBlockTourStep] = useState(0);
  const [blockTourKey, setBlockTourKey] = useState(0);

  // Mini-tour: Reschedule Type
  const [runRescheduleTour, setRunRescheduleTour] = useState(false);
  const [rescheduleTourStep, setRescheduleTourStep] = useState(0);
  const [rescheduleTourKey, setRescheduleTourKey] = useState(0);

  // Mini-tour: "Configurá tu disponibilidad real" — solo para NZ en estado NEW, se
  // dispara después del modal obligatorio que reemplaza al resto de la guía para ellas.
  const [runConfigureNowTour, setRunConfigureNowTour] = useState(false);
  const [configureNowTourStep, setConfigureNowTourStep] = useState(0);
  const [configureNowTourKey, setConfigureNowTourKey] = useState(0);
  const [showMandatoryCtaModal, setShowMandatoryCtaModal] = useState(false);
  // Modal de bienvenida mostrado ANTES de forzar la guía a la cohorte NZ/estado NEW.
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);

  // ─── Onboarding init: arranque forzado para la cohorte NZ/estado NEW ──────
  // Separado del init general de abajo porque `isMandatoryScheduleOnboarding` depende
  // de `psychologist`, que puede resolver DESPUÉS del primer render (fetch async) — si
  // viviera en el efecto de una sola vez de abajo (deps `[]`), un valor `false` inicial
  // quedaría fijo para siempre y nunca se forzaría la guía. Además, para esta cohorte
  // ignoramos por completo el flag "done" y el paso guardado: esas claves de localStorage
  // no están scopeadas por psicóloga, así que pueden haber quedado en true por haberse
  // completado antes con OTRA cuenta en el mismo navegador (típico al testear con
  // impersonación).
  const forcedTourStartedRef = useRef(false);
  useEffect(() => {
    if (!isMandatoryScheduleOnboarding || forcedTourStartedRef.current) return;
    if (runTour || runBookingTour) return;
    forcedTourStartedRef.current = true;
    // Antes de arrancar la guía forzada, mostramos el modal de bienvenida — la guía
    // recién arranca cuando confirma ahí (ver handleWelcomeModalConfirm).
    setShowWelcomeModal(true);
  }, [isMandatoryScheduleOnboarding, runTour, runBookingTour]);

  /** Confirmá el modal de bienvenida ("¡Vamos!"): lo cierra y recién ahí arranca la
   *  guía forzada de onboarding para la cohorte NZ/estado NEW. */
  const handleWelcomeModalConfirm = useCallback(() => {
    setShowWelcomeModal(false);
    clearStep(STEP_KEYS.calendarConfig);
    clearStep(STEP_KEYS.calendarBooking);
    setCanSkipTour(false);
    setStepIndex(0);
    setTimeout(() => setRunTour(true), 250);
  }, []);

  // ─── Onboarding init ─────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    waitForOnboardingSync().then(() => {
      if (cancelled) return;
      try {
        if (isMandatoryScheduleOnboarding) {
          // El arranque de esta cohorte lo maneja el efecto de arriba.
          return;
        }
        const done = localStorage.getItem("psi-calendar-tour-done");
        if (!done) {
          setCanSkipTour(false);
          const saved = getSavedStep(STEP_KEYS.calendarConfig);
          setTimeout(() => {
            if (cancelled) return;
            if (saved === 1) {
              setConfigDropdownOpen(true);
              setRunTour(true);
            } else if (saved >= 2) {
              setCalendarEditMode(true);
              const waitForGrid = () => {
                if (cancelled) return;
                if (document.getElementById("calendar-interactive-grid")) {
                  setRunTour(true);
                } else {
                  requestAnimationFrame(waitForGrid);
                }
              };
              requestAnimationFrame(waitForGrid);
            } else {
              setRunTour(true);
            }
          }, 250);
        } else {
          setCanSkipTour(true);
          clearStep(STEP_KEYS.calendarConfig);
          clearStep(STEP_KEYS.calendarBooking);
        }
      } catch {
        /* noop */
      }
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist step — config tour
  useEffect(() => {
    if (runTour) saveStep(STEP_KEYS.calendarConfig, stepIndex);
  }, [stepIndex, runTour]);

  // Persist step — booking tour
  useEffect(() => {
    if (runBookingTour) saveStep(STEP_KEYS.calendarBooking, bookingStepIndex);
  }, [bookingStepIndex, runBookingTour]);

  // Notify parent when any tour is running
  useEffect(() => {
    onTourRunningChange?.(
      runTour ||
        shouldStartBookingTour ||
        runBookingTour ||
        runConfigureNowTour ||
        showMandatoryCtaModal ||
        showWelcomeModal,
    );
  }, [
    runTour,
    runBookingTour,
    shouldStartBookingTour,
    runConfigureNowTour,
    showMandatoryCtaModal,
    showWelcomeModal,
    onTourRunningChange,
  ]);

  // ─── Global window flags ─────────────────────────────────────────────────
  useEffect(() => {
    try {
      (window as unknown as { __psiTourLockOverlay?: boolean }).__psiTourLockOverlay =
        runBookingTour;
    } catch {
      /* noop */
    }
    return () => {
      try {
        (window as unknown as { __psiTourLockOverlay?: boolean }).__psiTourLockOverlay = false;
      } catch {
        /* noop */
      }
    };
  }, [runBookingTour]);

  useEffect(() => {
    try {
      const mock = runTour && stepIndex === 8 && !mockSaveCompleted;
      (window as unknown as { __psiOnboardingMockCreate?: boolean }).__psiOnboardingMockCreate =
        mock;
    } catch {
      /* noop */
    }
    return () => {
      try {
        (
          window as unknown as { __psiOnboardingMockCreate?: boolean }
        ).__psiOnboardingMockCreate = false;
      } catch {
        /* noop */
      }
    };
  }, [runTour, stepIndex, mockSaveCompleted]);

  // ─── psi-work-range-created (interactive step 8 save) ────────────────────
  useEffect(() => {
    const handler = () => {
      if (!runTour || stepIndex !== 8) return;
      setShowUnsavedModal(false);
      setScheduleHasChanges(false);
      shouldStartBookingTourRef.current = true;
      clearStep(STEP_KEYS.calendarConfig);
      const wasCalendarDone = localStorage.getItem("psi-calendar-tour-done") === "1";
      try {
        localStorage.setItem("psi-calendar-tour-done", "1");
      } catch {
        /* noop */
      }
      try {
        localStorage.setItem("psi-reschedule-type-tour-done", "1");
      } catch {
        /* noop */
      }
      checkAndMarkOnboardingComplete();
      if (!wasCalendarDone) {
        markTourCompleteOnServer("psi-calendar-tour-done");
        markTourCompleteOnServer("psi-reschedule-type-tour-done");
      }
      // Auto-transition to booking tour without waiting for manual panel close
      setTimeout(() => {
        try {
          window.dispatchEvent(new CustomEvent("psi-work-schedule-reset"));
        } catch {
          /* noop */
        }
        setMockSaveCompleted(true);
        setRunTour(false);
        window.dispatchEvent(new CustomEvent("psi-work-schedule-cancel", { detail: { wasSaved: true } }));
      }, 500);
    };
    window.addEventListener("psi-work-range-created", handler as EventListener);
    return () => window.removeEventListener("psi-work-range-created", handler as EventListener);
  }, [runTour, stepIndex, setScheduleHasChanges, setShowUnsavedModal]);

  // ─── psi-work-schedule-cancel ─────────────────────────────────────────────
  useEffect(() => {
    const handler = (e: Event) => {
      const wasSaved = (e as CustomEvent<{ wasSaved?: boolean }>).detail?.wasSaved;
      const pendingBooking = shouldStartBookingTourRef.current;
      const wasTourRunning = runTour;

      if (wasTourRunning) {
        try {
          window.dispatchEvent(new CustomEvent("psi-hide-mock-reschedule"));
        } catch {
          /* noop */
        }
        setRunTour(false);
        clearStep(STEP_KEYS.calendarConfig);
        const wasAlreadyDone = localStorage.getItem("psi-calendar-tour-done") === "1";
        try {
          localStorage.setItem("psi-calendar-tour-done", "1");
        } catch {
          /* noop */
        }
        try {
          localStorage.setItem("psi-reschedule-type-tour-done", "1");
        } catch {
          /* noop */
        }
        checkAndMarkOnboardingComplete();
        if (!wasAlreadyDone) {
          markTourCompleteOnServer("psi-calendar-tour-done");
          markTourCompleteOnServer("psi-reschedule-type-tour-done");
        }
      }

      if (scheduleHasChanges && !wasSaved) {
        if (wasTourRunning || pendingBooking) setShouldStartBookingTour(true);
        setShowUnsavedModal(true);
      } else {
        if (wasTourRunning || pendingBooking) {
          setShouldStartBookingTour(false);
          shouldStartBookingTourRef.current = false;
          setCalendarEditMode(false);
          setOpenBlockModalTick(false);
          setSpecificDayDate(null);
          if (isMandatoryScheduleOnboarding && wasTourRunning) {
            // NZ recién creada: no seguimos con el tour de agendamiento simulado — el
            // horario que acaba de "guardar" era ficticio, así que en su lugar le pedimos
            // directamente que cargue su disponibilidad real.
            setTimeout(() => setShowMandatoryCtaModal(true), 500);
          } else {
            setTimeout(() => {
              setRunBookingTour(true);
              setBookingStepIndex(0);
              saveStep(STEP_KEYS.calendarBooking, 0);
            }, 400);
          }
        } else {
          setIsExitingEdit(true);
          setTimeout(() => {
            setIsExitingEdit(false);
            setCalendarEditMode(false);
            setOpenBlockModalTick(false);
            setSpecificDayDate(null);
          }, 1000);
        }
      }
    };
    window.addEventListener("psi-work-schedule-cancel", handler);
    return () => window.removeEventListener("psi-work-schedule-cancel", handler);
  }, [
    scheduleHasChanges,
    runTour,
    setCalendarEditMode,
    setOpenBlockModalTick,
    setSpecificDayDate,
    setIsExitingEdit,
    setShowUnsavedModal,
    isMandatoryScheduleOnboarding,
  ]);

  // ─── Booking tour: event-driven step advances ─────────────────────────────
  useEffect(() => {
    if (!runBookingTour) return;

    const handlePatientConfirmed = () => {
      try {
        (window as unknown as { __psiBookingMockPatients?: boolean }).__psiBookingMockPatients =
          false;
      } catch {
        /* noop */
      }
      setBookingStepIndex(3);
    };

    const handleMockCompleted = () => {
      try {
        window.dispatchEvent(new CustomEvent("psi-hide-mock-availability"));
      } catch {
        /* noop */
      }
      setBookingStepIndex(5);
    };

    window.addEventListener("psi-booking-patient-confirmed", handlePatientConfirmed);
    window.addEventListener("psi-booking-mock-completed", handleMockCompleted);
    return () => {
      window.removeEventListener("psi-booking-patient-confirmed", handlePatientConfirmed);
      window.removeEventListener("psi-booking-mock-completed", handleMockCompleted);
    };
  }, [runBookingTour]);

  // Step 3→4: detect BookSessionModal appearing
  useEffect(() => {
    if (!runBookingTour || bookingStepIndex !== 3) {
      if (bookingSlotObsRef.current) {
        bookingSlotObsRef.current.disconnect();
        bookingSlotObsRef.current = null;
      }
      return;
    }
    const advanceIfPresent = () => {
      const modal = document.getElementById("book-session-modal-content");
      if (modal) {
        setBookingStepIndex(4);
        return true;
      }
      return false;
    };
    if (advanceIfPresent()) return;
    const observer = new MutationObserver(() => {
      if (advanceIfPresent()) {
        observer.disconnect();
        bookingSlotObsRef.current = null;
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    bookingSlotObsRef.current = observer;
    return () => {
      observer.disconnect();
      bookingSlotObsRef.current = null;
    };
  }, [runBookingTour, bookingStepIndex]);

  // Step 4→3: detect BookSessionModal disappearing (user cancelled)
  useEffect(() => {
    if (!runBookingTour || bookingStepIndex !== 4) {
      if (bookingCancelObsRef.current) {
        bookingCancelObsRef.current.disconnect();
        bookingCancelObsRef.current = null;
      }
      bookingConfirmedRef.current = false;
      return;
    }
    bookingConfirmedRef.current = false;
    const handleMockStarted = () => {
      bookingConfirmedRef.current = true;
    };
    window.addEventListener("psi-booking-mock-started", handleMockStarted);
    const observer = new MutationObserver(() => {
      if (bookingConfirmedRef.current) return;
      const modal = document.getElementById("book-session-modal-content");
      if (!modal) {
        observer.disconnect();
        bookingCancelObsRef.current = null;
        try {
          window.dispatchEvent(new CustomEvent("psi-show-mock-availability"));
        } catch {
          /* noop */
        }
        setTimeout(() => setBookingStepIndex(3), 50);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    bookingCancelObsRef.current = observer;
    return () => {
      observer.disconnect();
      bookingCancelObsRef.current = null;
      window.removeEventListener("psi-booking-mock-started", handleMockStarted);
    };
  }, [runBookingTour, bookingStepIndex]);

  // ─── Mini-tour triggers ───────────────────────────────────────────────────
  useEffect(() => {
    if (specificDayDate === null || runTour || runBookingTour || runBlockTour) return;
    try {
      if (localStorage.getItem("psi-specific-day-tour-done") === "1") return;
    } catch {
      return;
    }
    const timer = setTimeout(() => {
      if (document.getElementById("specific-day-panel")) {
        setSpecificDayTourStep(0);
        setSpecificDayTourKey((prev) => prev + 1);
        setRunSpecificDayTour(true);
      }
    }, 600);
    return () => clearTimeout(timer);
  }, [specificDayDate, runTour, runBookingTour, runBlockTour]);

  useEffect(() => {
    if (specificDayDate === null && runSpecificDayTour) {
      setRunSpecificDayTour(false);
    }
  }, [specificDayDate, runSpecificDayTour]);

  useEffect(() => {
    if (!openBlockModalTick || runTour || runBookingTour || runSpecificDayTour) return;
    try {
      if (localStorage.getItem("psi-block-schedule-tour-done") === "1") return;
    } catch {
      return;
    }
    const timer = setTimeout(() => {
      if (document.getElementById("block-schedule-panel")) {
        setBlockTourStep(0);
        setBlockTourKey((prev) => prev + 1);
        setRunBlockTour(true);
      }
    }, 600);
    return () => clearTimeout(timer);
  }, [openBlockModalTick, runTour, runBookingTour, runSpecificDayTour]);

  useEffect(() => {
    if (!openBlockModalTick && runBlockTour) {
      setRunBlockTour(false);
    }
  }, [openBlockModalTick, runBlockTour]);

  useEffect(() => {
    if (
      (!calendarEditMode && specificDayDate === null) ||
      runTour ||
      runBookingTour ||
      runSpecificDayTour ||
      runBlockTour
    )
      return;
    try {
      if (localStorage.getItem("psi-calendar-tour-done") !== "1") return;
      if (localStorage.getItem("psi-reschedule-type-tour-done") === "1") return;
    } catch {
      return;
    }
    const timer = setTimeout(() => {
      if (
        document.getElementById("work-schedule-panel") ||
        document.getElementById("specific-day-panel")
      ) {
        try {
          window.dispatchEvent(new CustomEvent("psi-show-mock-reschedule"));
        } catch {
          /* noop */
        }
        setTimeout(() => {
          const mockRow = document.getElementById("onboarding-reschedule-mock-row");
          if (mockRow) {
            const scrollParent = mockRow.closest(".overflow-y-auto");
            if (scrollParent) scrollParent.scrollTop = scrollParent.scrollHeight;
          }
        }, 100);
        setRescheduleTourStep(0);
        setRescheduleTourKey((prev) => prev + 1);
        setRunRescheduleTour(true);
      }
    }, 600);
    return () => clearTimeout(timer);
  }, [calendarEditMode, specificDayDate, runTour, runBookingTour, runSpecificDayTour, runBlockTour]);

  useEffect(() => {
    if (!calendarEditMode && specificDayDate === null && runRescheduleTour) {
      try {
        window.dispatchEvent(new CustomEvent("psi-hide-mock-reschedule"));
      } catch {
        /* noop */
      }
      setRunRescheduleTour(false);
    }
  }, [calendarEditMode, specificDayDate, runRescheduleTour]);

  // ─── Config tour steps ────────────────────────────────────────────────────
  const steps: Step[] = useMemo(
    () => [
      {
        target: "#btn-configurar-horarios",
        content:
          "Para configurar tus horarios de trabajo, hacé click aquí. Esto te permitirá definir los rangos disponibles donde nuestros pacientes podrán agendar.",
        disableBeacon: true,
        spotlightClicks: false,
        disableOverlayClose: true,
        spotlightPadding: 6,
        placement: "bottom" as const,
      },
      {
        target: "#config-dropdown-options",
        content:
          "Desde acá podés elegir qué tipo de configuración hacer: horario fijo semanal, un día específico puntual, o bloquear espacios donde no querés recibir sesiones.",
        disableBeacon: true,
        spotlightClicks: false,
        disableOverlayClose: true,
        spotlightPadding: 6,
        placement: "bottom" as const,
      },
      {
        target: "#calendar-interactive-grid",
        content:
          "En la vista de configuración podés hacer click o arrastrar sobre el calendario para crear un rango disponible. Los pacientes podrán agendar solo dentro de estos rangos.",
        disableBeacon: true,
        spotlightClicks: false,
        disableOverlayClose: true,
        spotlightPadding: 6,
      },
      {
        target: "#work-schedule-panel",
        content:
          "Desde este panel podés ver y editar todos tus horarios semanales. Cada día tiene sus propios rangos horarios que se repiten semana a semana.",
        disableBeacon: true,
        spotlightClicks: false,
        disableOverlayClose: true,
        spotlightPadding: 6,
        placement: "right" as const,
      },
      {
        target: "#onboarding-panel-day-row",
        content: (
          <span>
            Elegí la hora de inicio y fin con los selectores. Podés sumar más rangos con{" "}
            <span style={{ color: "#6D57BC", fontWeight: 700 }}>+ Agregar horario</span>{" "}
            o eliminar uno con la{" "}
            <span style={{ color: "#ef4444", fontWeight: 700 }}>✕</span> de la derecha.
          </span>
        ),
        disableBeacon: true,
        spotlightClicks: false,
        disableOverlayClose: true,
        disableScrolling: true,
        spotlightPadding: 6,
        placement: "right" as const,
      },
      {
        target: "#onboarding-panel-day-header",
        content: (
          <span>
            Con{" "}
            <span style={{ display: "inline-flex", alignItems: "center", verticalAlign: "middle", margin: "0 2px" }}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="0.5" y="0.5" width="15" height="15" rx="3.5" fill="#6D57BC" stroke="#6D57BC" />
                <path d="M3.5 8L6.5 11L12.5 5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>{" "}
            habilitás o deshabilitás un día completo. Con el ícono de copiar podés replicar los horarios de un día a toda la semana.
          </span>
        ),
        disableBeacon: true,
        spotlightClicks: false,
        disableOverlayClose: true,
        spotlightPadding: 6,
        placement: "right" as const,
      },
      {
        target: "#onboarding-reschedule-mock-row",
        content: (
          <div>
            <p style={{ margin: "0 0 0.5rem" }}>
              Al lado de cada rango horario hay un botón que te permite cambiar su tipo:
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", margin: "0.5rem 0" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: "9999px", fontSize: "0.7rem", fontWeight: 700, background: "#f5f3ff", color: "#7c3aed", border: "1px solid #c4b5fd" }}>Est.</span>
                <span style={{ fontSize: "0.85rem" }}><b>Estándar</b> — disponible para pacientes nuevos y activos.</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: "9999px", fontSize: "0.7rem", fontWeight: 700, background: "#fffbeb", color: "#b45309", border: "1px solid #fcd34d" }}>Repr.</span>
                <span style={{ fontSize: "0.85rem" }}><b>Reprogramación</b> — solo para reprogramar sesiones existentes, no se ofrece a nuevos pacientes.</span>
              </div>
            </div>
          </div>
        ),
        disableBeacon: true,
        spotlightClicks: false,
        disableOverlayClose: true,
        spotlightPadding: 6,
        placement: "right" as const,
      },
      {
        target: "#calendar-interactive-grid",
        content: (
          <div>
            <p style={{ margin: "0 0 0.5rem" }}>
              Los horarios de color ámbar son exclusivos para reprogramación de sesiones.
            </p>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.6rem", margin: "0.4rem 0 0" }}>
              <div style={{ display: "inline-block", padding: "0.4rem 1rem", borderRadius: 6, background: "rgba(180, 83, 9, 0.18)", border: "1px solid rgba(180, 83, 9, 0.35)", flexShrink: 0 }}>
                <span style={{ fontSize: "0.8rem", fontWeight: 700, color: "#92400e" }}>09:00 — 12:00</span>
              </div>
              <span style={{ fontSize: "0.78rem", color: "#6b7280" }}>Así se verá en tu calendario</span>
            </div>
          </div>
        ),
        disableBeacon: true,
        spotlightClicks: false,
        disableOverlayClose: true,
        spotlightPadding: 6,
      },
      {
        target: "#work-schedule-panel",
        content: isMandatoryScheduleOnboarding ? (
          <div>
            <p style={{ margin: "0 0 0.5rem" }}>
              Ahora te toca a vos! Elegí un día, configurá un rango horario y hacé click en
              &quot;Guardar cambios&quot;.
            </p>
            <p style={{ margin: 0 }}>
              <b>Este horario es ficticio</b>, no se te va a cargar realmente: es solo para
              que aprendas cómo se hace.
            </p>
          </div>
        ) : (
          "Ahora te toca a vos! Elegí un día, configurá un rango horario y hacé click en \"Guardar cambios\". Este paso no tiene botón de continuar."
        ),
        disableBeacon: true,
        spotlightClicks: true,
        disableOverlayClose: true,
        spotlightPadding: 6,
        placement: "right" as const,
      },
    ],
    [isMandatoryScheduleOnboarding],
  );

  // ─── Config tour callback ─────────────────────────────────────────────────
  const onTourCallback = useCallback(
    (data: CallBackProps) => {
      const { status, index, type, action } = data;

      if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
        if (shouldStartBookingTourRef.current) return;
        try {
          (window as unknown as { __psiBookingMockPatients?: boolean }).__psiBookingMockPatients =
            false;
        } catch {
          /* noop */
        }
        try {
          (window as unknown as { __psiTourLockOverlay?: boolean }).__psiTourLockOverlay = false;
        } catch {
          /* noop */
        }
        try {
          (
            window as unknown as { __psiOnboardingMockCreate?: boolean }
          ).__psiOnboardingMockCreate = false;
        } catch {
          /* noop */
        }
        try {
          window.dispatchEvent(new CustomEvent("psi-hide-mock-availability"));
        } catch {
          /* noop */
        }
        try {
          window.dispatchEvent(new CustomEvent("psi-hide-mock-reschedule"));
        } catch {
          /* noop */
        }
        setCancelSchedulingTick(Date.now());
        setCalendarEditMode(false);
        setRunTour(false);
        clearStep(STEP_KEYS.calendarConfig);
        const wasAlreadyDone = localStorage.getItem("psi-calendar-tour-done") === "1";
        try {
          localStorage.setItem("psi-calendar-tour-done", "1");
        } catch {
          /* noop */
        }
        try {
          localStorage.setItem("psi-reschedule-type-tour-done", "1");
        } catch {
          /* noop */
        }
        checkAndMarkOnboardingComplete();
        if (!wasAlreadyDone) {
          markTourCompleteOnServer("psi-calendar-tour-done");
          markTourCompleteOnServer("psi-reschedule-type-tour-done");
        }
        return;
      }

      try {
        const t = (data as unknown as { type?: string }).type;
        if (t === EVENTS.TARGET_NOT_FOUND && (index === 1 || index === 6)) {
          window.setTimeout(() => setTourKey((prev) => prev + 1), 60);
          return;
        }
      } catch {
        /* noop */
      }

      if (type === "step:after" && index === 0 && action === "next") {
        setConfigDropdownOpen(true);
        setStepIndex(1);
      }
      if (type === "step:after" && index === 1 && action === "next") {
        setConfigDropdownOpen(false);
        if (!calendarEditMode) setCalendarEditMode(true);
        setStepIndex(2);
      }
      if (type === "step:after" && index === 2 && action === "next") setStepIndex(3);
      if (type === "step:after" && index === 3 && action === "next") {
        try {
          window.dispatchEvent(new CustomEvent("psi-show-mock-reschedule"));
        } catch {
          /* noop */
        }
        setTimeout(() => {
          const mockRow = document.getElementById("onboarding-panel-day-row");
          if (mockRow) {
            const scrollParent = mockRow.closest(".overflow-y-auto");
            if (scrollParent) scrollParent.scrollTop = scrollParent.scrollHeight;
          }
        }, 100);
        setStepIndex(4);
      }
      if (type === "step:after" && index === 4 && action === "next") setStepIndex(5);
      if (type === "step:after" && index === 5 && action === "next") setStepIndex(6);
      if (type === "step:after" && index === 6 && action === "next") setStepIndex(7);
      if (type === "step:after" && index === 7 && action === "next") {
        try {
          window.dispatchEvent(new CustomEvent("psi-hide-mock-reschedule"));
        } catch {
          /* noop */
        }
        setStepIndex(8);
      }

      if (type === "step:after" && action === "prev") {
        if (index === 1) { setConfigDropdownOpen(false); setStepIndex(0); return; }
        if (index === 2) { setCalendarEditMode(false); setConfigDropdownOpen(true); setStepIndex(1); return; }
        if (index === 3) { setStepIndex(2); return; }
        if (index === 4) { try { window.dispatchEvent(new CustomEvent("psi-hide-mock-reschedule")); } catch { /* noop */ } setStepIndex(3); return; }
        if (index === 5) { setStepIndex(4); return; }
        if (index === 6) { setStepIndex(5); return; }
        if (index === 7) { setStepIndex(6); return; }
        if (index === 8) { try { window.dispatchEvent(new CustomEvent("psi-show-mock-reschedule")); } catch { /* noop */ } setStepIndex(7); return; }
      }
    },
    [calendarEditMode, setCalendarEditMode, setConfigDropdownOpen, setCancelSchedulingTick],
  );

  // ─── Booking tour callback ────────────────────────────────────────────────
  const onBookingTourCallback = useCallback(
    (data: CallBackProps) => {
      const { status, index, type, action } = data;

      const finishBookingTour = () => {
        try {
          (window as unknown as { __psiBookingMockPatients?: boolean }).__psiBookingMockPatients =
            false;
        } catch {
          /* noop */
        }
        try {
          (window as unknown as { __psiTourLockOverlay?: boolean }).__psiTourLockOverlay = false;
        } catch {
          /* noop */
        }
        try {
          window.dispatchEvent(new CustomEvent("psi-hide-mock-availability"));
        } catch {
          /* noop */
        }
        try {
          window.dispatchEvent(new CustomEvent("psi-close-work-range-and-exit"));
        } catch {
          /* noop */
        }
        setCancelSchedulingTick(Date.now());
        setCalendarEditMode(false);
        setRunBookingTour(false);
        setBookingStepIndex(0);
        clearStep(STEP_KEYS.calendarBooking);
        const wasAlreadyDone = localStorage.getItem("psi-calendar-tour-done") === "1";
        try {
          localStorage.setItem("psi-calendar-tour-done", "1");
        } catch {
          /* noop */
        }
        try {
          localStorage.setItem("psi-reschedule-type-tour-done", "1");
        } catch {
          /* noop */
        }
        checkAndMarkOnboardingComplete();
        if (!wasAlreadyDone) {
          markTourCompleteOnServer("psi-calendar-tour-done");
          markTourCompleteOnServer("psi-reschedule-type-tour-done");
        }
      };

      if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
        finishBookingTour();
        return;
      }

      if (type === "step:after" && index === 0 && action === "next") {
        setOpenScheduleModalTick(Date.now());
        try {
          window.dispatchEvent(new CustomEvent("psi-show-mock-availability"));
        } catch {
          /* noop */
        }
        if (bookingObsRef.current) {
          bookingObsRef.current.disconnect();
          bookingObsRef.current = null;
        }
        const advanceIfPresent = () => {
          const modal = document.getElementById("select-patient-type-modal-content");
          if (modal) {
            setBookingStepIndex(1);
            try {
              (
                window as unknown as { __psiBookingMockPatients?: boolean }
              ).__psiBookingMockPatients = true;
            } catch {
              /* noop */
            }
            return true;
          }
          return false;
        };
        if (!advanceIfPresent()) {
          const observer = new MutationObserver(() => {
            if (advanceIfPresent()) {
              if (bookingObsRef.current) bookingObsRef.current.disconnect();
              bookingObsRef.current = null;
            }
          });
          observer.observe(document.body, { childList: true, subtree: true });
          bookingObsRef.current = observer;
        }
      }

      if (type === "step:after" && index === 1 && action === "next") {
        setBookingStepIndex(2);
      }

      if (type === "step:after" && index === 5 && action === "next") {
        finishBookingTour();
      }

      if (type === "step:after" && action === "prev") {
        if (index === 1) {
          const modal = document.getElementById("select-patient-type-modal-content");
          if (modal?.parentElement) (modal.parentElement as HTMLElement).click();
          setBookingStepIndex(0);
          return;
        }
        if (index === 2) {
          try {
            (
              window as unknown as { __psiBookingMockPatients?: boolean }
            ).__psiBookingMockPatients = true;
          } catch {
            /* noop */
          }
          setBookingStepIndex(1);
          return;
        }
        if (index === 3) {
          setOpenScheduleModalTick(Date.now());
          try {
            (
              window as unknown as { __psiBookingMockPatients?: boolean }
            ).__psiBookingMockPatients = true;
          } catch {
            /* noop */
          }
          const tryAdvance = () => {
            if (document.getElementById("select-patient-type-modal-content")) {
              setBookingStepIndex(2);
              return;
            }
            const obs = new MutationObserver(() => {
              if (document.getElementById("select-patient-type-modal-content")) {
                obs.disconnect();
                setBookingStepIndex(2);
              }
            });
            obs.observe(document.body, { childList: true, subtree: true });
          };
          tryAdvance();
          return;
        }
        if (index === 4) {
          const modal = document.getElementById("book-session-modal-content");
          if (modal?.parentElement) (modal.parentElement as HTMLElement).click();
          try {
            window.dispatchEvent(new CustomEvent("psi-show-mock-availability"));
          } catch {
            /* noop */
          }
          setBookingStepIndex(3);
          return;
        }
        if (index === 5) {
          try {
            window.dispatchEvent(new CustomEvent("psi-show-mock-booking-modal"));
          } catch {
            /* noop */
          }
          setBookingStepIndex(4);
          return;
        }
      }
    },
    [setCalendarEditMode, setCancelSchedulingTick, setOpenScheduleModalTick],
  );

  // ─── Helpers for CalendarTab ──────────────────────────────────────────────
  const replayTour = useCallback(() => {
    clearStep(STEP_KEYS.calendarConfig);
    clearStep(STEP_KEYS.calendarBooking);
    setTourKey((k) => k + 1);
    setStepIndex(0);
    setBookingStepIndex(0);
    setRunBookingTour(false);
    setMockSaveCompleted(false);
    setRunTour(true);
  }, []);

  const handleTourDiscardExit = useCallback(() => {
    if (stepIndex === 7) setStepIndex(0);
    else setStepIndex(8);
  }, [stepIndex]);

  /** Confirmá el CTA obligatorio ("Vamos a configurarla"): cierra el modal y lanza el
   *  mini-tour de un solo paso que señala el botón real "Configurar horarios". */
  const handleMandatoryCtaConfirm = useCallback(() => {
    setShowMandatoryCtaModal(false);
    setConfigureNowTourStep(0);
    setConfigureNowTourKey((k) => k + 1);
    setTimeout(() => setRunConfigureNowTour(true), 200);
  }, []);

  const startBookingAfterDiscard = useCallback(() => {
    setShouldStartBookingTour(false);
    shouldStartBookingTourRef.current = false;
    setCalendarEditMode(false);
    setOpenBlockModalTick(false);
    setSpecificDayDate(null);
    setTimeout(() => {
      setRunBookingTour(true);
      setBookingStepIndex(0);
      saveStep(STEP_KEYS.calendarBooking, 0);
    }, 400);
  }, [setCalendarEditMode, setOpenBlockModalTick, setSpecificDayDate]);

  return {
    // Config tour
    runTour,
    stepIndex,
    tourKey,
    canSkipTour,
    steps,
    onTourCallback,
    // Booking tour
    runBookingTour,
    bookingStepIndex,
    onBookingTourCallback,
    // Mini-tours
    runSpecificDayTour,
    specificDayTourStep,
    setSpecificDayTourStep,
    specificDayTourKey,
    setRunSpecificDayTour,
    runBlockTour,
    blockTourStep,
    setBlockTourStep,
    blockTourKey,
    setRunBlockTour,
    runRescheduleTour,
    rescheduleTourStep,
    setRescheduleTourStep,
    rescheduleTourKey,
    setRescheduleTourKey,
    setRunRescheduleTour,
    // Mini-tour: "Configurá tu disponibilidad real" (solo NZ estado NEW)
    runConfigureNowTour,
    configureNowTourStep,
    setConfigureNowTourStep,
    configureNowTourKey,
    setRunConfigureNowTour,
    showMandatoryCtaModal,
    handleMandatoryCtaConfirm,
    showWelcomeModal,
    handleWelcomeModalConfirm,
    // Flags
    isOnboardingMockMode: runTour && stepIndex >= 2 && !mockSaveCompleted,
    // Helpers
    replayTour,
    handleTourDiscardExit,
    startBookingAfterDiscard,
    shouldStartBookingTour,
    setShouldStartBookingTour,
  };
}
